import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconHash, IconScale, IconSparkles } from "@tabler/icons-react";
import { AnnouncementFeature, AnnouncementShell } from "./AnnouncementShell";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { heroFor, type AnnouncementProps, type LocalizedHero } from "./types";
import heroEn from "../../assets/images/announcements/feed.en.webp";
import heroFr from "../../assets/images/announcements/feed.fr.webp";

/**
 * The hero carries the topic chips, and the chips are words: the English frame
 * reads "Heart Failure" where the French one reads "Insuffisance cardiaque".
 * A language with no artwork of its own falls back to English.
 */
const HERO: LocalizedHero = {
  en: heroEn,
  fr: heroFr,
  it: null,
  de: null,
  es: null,
  pt: null,
  ar: null,
  nl: null,
};

/**
 * "Your feed starts with one question" — Figma 6693:10156.
 *
 * Two buttons, unlike the GPT sheet's one: the offer is to go and pick topics,
 * and a reader who does not want to do that now needs somewhere to put their
 * thumb other than the close cross. "Not now" is a DISMISSAL, not an action, so
 * it never counts towards the announcement's conversion rate.
 */
export function FeedAnnouncement({
  isOpen,
  onDismiss,
  onAct,
}: AnnouncementProps): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const history = useHistory();

  return (
    <AnnouncementShell
      presentation="sheet"
      maxWidth="wide"
      isOpen={isOpen}
      onDismiss={onDismiss}
      media={
        <img
          src={heroFor(HERO, activeLang)}
          alt=""
          className="block h-63 w-full object-cover"
        />
      }
      label={t("New")}
      title={t("Your feed starts with one question")}
      actions={[
        {
          label: t("Follow my topics"),
          variant: "primary",
          onPress: () => {
            onAct("follow-topics");
            history.push("/specialties/manage");
          },
        },
        {
          label: t("Not now"),
          variant: "outline",
          onPress: () => {
            onDismiss("decline_button");
          },
        },
      ]}
      features={
        <>
          <AnnouncementFeature
            icon={<IconHash size={17} stroke={1.5} />}
            title={t("Follow the topics that matter to you")}
            description={t(
              "Your feed is built from them, and you can change them at any time."
            )}
          />
          <AnnouncementFeature
            icon={<IconSparkles size={17} stroke={1.5} />}
            title={t("Keep reading, it gets sharper")}
            description={t(
              "Your feed adapts to the articles you open and save, and to the topics you follow or drop."
            )}
          />
          <AnnouncementFeature
            icon={<IconScale size={17} stroke={1.5} />}
            title={t("Judge a paper before you open it")}
            description={t(
              "Two signals, kept apart. The journal is graded A+ to E against others in its specialty. The study is run through the standard risk-of-bias checklist for its design: trial, cohort, review. Tap either to open the scoring."
            )}
          />
        </>
      }
    />
  );
}
